export const formatCartMoney = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '₹0.00';
  return `₹${n.toFixed(2)}`;
};

export const formatCartDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return null;
};

/** Normalize admin cart list row from API (camelCase or PascalCase). */
export const normalizeCartListItem = (row) => ({
  cartId: pick(row, 'cartId', 'CartId'),
  userId: pick(row, 'userId', 'UserId'),
  userName: pick(row, 'userName', 'UserName', 'Username', 'username') || '—',
  cartStatus: pick(row, 'cartStatus', 'CartStatus') || 'Added',
  createdAt: pick(row, 'createdAt', 'CreatedAt'),
  totalQuantity: Number(pick(row, 'totalQuantity', 'TotalQuantity') ?? 0),
  totalAmount: Number(pick(row, 'totalAmount', 'TotalAmount', 'grandTotal', 'GrandTotal') ?? 0),
});

/** GET /Cart/Admin/GetAll — array or wrapped payload. */
export const parseCartListResponse = (data) => {
  if (Array.isArray(data)) {
    return data.map(normalizeCartListItem);
  }
  if (Array.isArray(data?.data)) {
    return data.data.map(normalizeCartListItem);
  }
  if (Array.isArray(data?.Data)) {
    return data.Data.map(normalizeCartListItem);
  }
  return [];
};

export const normalizeCartDetailItem = (row) => {
  const unitPrice = Number(pick(row, 'price', 'Price', 'unitPrice', 'UnitPrice') ?? 0);
  const originalPrice = Number(
    pick(row, 'originalPrice', 'OriginalPrice', 'originalUnitPrice', 'OriginalUnitPrice')
    ?? unitPrice
    ?? 0
  );
  const isBundleItem = pick(row, 'isBundleItem', 'IsBundleItem') === true
    || pick(row, 'isBundleItem', 'IsBundleItem') === 1
    || String(pick(row, 'isBundleItem', 'IsBundleItem') ?? '').toLowerCase() === 'true';
  const displayPrice = isBundleItem ? originalPrice : unitPrice;

  return {
    cartItemId: pick(row, 'cartItemId', 'CartItemId'),
    cartId: pick(row, 'cartId', 'CartId'),
    productId: pick(row, 'productId', 'ProductId'),
    productName: pick(row, 'productName', 'ProductName') || `Product #${pick(row, 'productId', 'ProductId')}`,
    quantity: Number(pick(row, 'quantity', 'Quantity') ?? 0),
    price: displayPrice,
    originalPrice,
    isBundleItem,
    bundleProductId: pick(row, 'bundleProductId', 'BundleProductId'),
    bundleGroupId: pick(row, 'bundleGroupId', 'BundleGroupId'),
    bundleGroupPrice: pick(row, 'bundleGroupPrice', 'BundleGroupPrice'),
    totalPrice: Number(
      pick(row, 'totalPrice', 'TotalPrice', 'lineTotal', 'LineTotal') ??
        Number(pick(row, 'quantity', 'Quantity') ?? 0) * displayPrice
    ),
    productImage: pick(row, 'imageUrl', 'ImageUrl', 'productImage', 'ProductImage') || '',
    createdAt: pick(row, 'createdAt', 'CreatedAt'),
  };
};

/** GET /Cart/Admin/GetDetail/{cartId} */
export const parseCartDetailResponse = (data) => {
  const root = data?.data ?? data?.Data ?? data;
  if (!root || typeof root !== 'object' || Array.isArray(root)) return null;

  const rawItems = root.items ?? root.Items ?? [];
  const items = (Array.isArray(rawItems) ? rawItems : []).map(normalizeCartDetailItem);

  return {
    cartId: pick(root, 'cartId', 'CartId'),
    userId: pick(root, 'userId', 'UserId'),
    userName: pick(root, 'userName', 'UserName', 'Username', 'username') || '—',
    cartStatus: pick(root, 'cartStatus', 'CartStatus') || 'Added',
    createdAt: pick(root, 'createdAt', 'CreatedAt'),
    totalQuantity: Number(pick(root, 'totalQuantity', 'TotalQuantity') ?? 0),
    totalAmount: Number(pick(root, 'totalAmount', 'TotalAmount', 'grandTotal', 'GrandTotal') ?? 0),
    items,
  };
};

export const filterCartList = (carts, search) => {
  const q = String(search || '').trim().toLowerCase();
  if (!q) return carts;
  return carts.filter((cart) =>
    [
      cart.cartId,
      cart.userId,
      cart.userName,
      cart.cartStatus,
      cart.totalQuantity,
      cart.totalAmount,
    ]
      .join(' ')
      .toLowerCase()
      .includes(q)
  );
};

export const sortCartList = (carts, sortKey, sortDir) => {
  const dir = sortDir === 'asc' ? 1 : -1;
  const keyMap = {
    CartId: 'cartId',
    Username: 'userName',
    CartStatus: 'cartStatus',
    CreatedAt: 'createdAt',
    GrandTotal: 'totalAmount',
    TotalQuantity: 'totalQuantity',
  };
  const field = keyMap[sortKey] || 'createdAt';

  return [...carts].sort((a, b) => {
    const av = a[field];
    const bv = b[field];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });
};

export const paginateList = (items, page, pageSize) => {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
};

export const sumItemsTotal = (items = []) =>
  items.reduce((sum, item) => sum + Number(item.totalPrice ?? 0), 0);
